import React, {
    ComponentPropsWithoutRef,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';
import Slider from 'react-slick';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { maskDecimal, unmaskNumber } from '@givebutter/util';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IProps } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import * as Form from 'components/form';
import { InputCurrencyIcon } from 'components/input';
import * as AuctionTooltip from 'components/pages/campaign-settings/campaign-auction/AuctionTooltip';
import { ItemFormData } from 'components/pages/campaign-settings/campaign-auction/items-form/types';
import {
    getAllowBids,
    useItemForm,
} from 'components/pages/campaign-settings/campaign-auction/items-form/useItemForm';
import { SponsorReservePriceInfo } from 'components/pages/campaign-settings/campaign-auction/sponsors/SponsorReservePriceInfo';
import { UseFormUpload } from 'components/upload';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { twMerge } from 'tailwind-merge';

const WysiwygConfig: IProps['init'] = {
    menubar: false,
    statusbar: false,
    plugins: [
        'autolink',
        'lists',
        'link',
        'searchreplace',
        'visualblocks',
        'insertdatetime',
        'help',
        'wordcount',
        'powerpaste',
        'emoticons',
        'canva',
        'image',
        'media',
    ],
    toolbar:
        'undo redo blocks align bold italic underline strikethrough hr forecolor link canva image media emoticons numlist bullist',
    content_style: Wysiwyg.Constants.APP_FONT,
    paste_data_images: false,
};

interface ItemDetailsFormProps {
    children: React.ReactNode;
    onAddingFile: (isAdding: boolean) => void;
    onCategoriesOpen: (isOpen: boolean) => void;
}

interface ItemDetailsContext {
    isAddingFile: boolean;
    setIsAddingFile: (isAdding: boolean) => void;
    onCategoriesOpen: (isOpen: boolean) => void;
}

const defaultContext: ItemDetailsContext = {
    isAddingFile: false,
    setIsAddingFile: () => {},
    onCategoriesOpen: () => {},
};

const ItemDetailsContext = createContext<ItemDetailsContext>(defaultContext);

const useItemDetailsForm = () => {
    const context = useContext(ItemDetailsContext);
    if (!context) {
        return defaultContext;
    }
    return context;
};

const ItemDetailsForm = ({
    children,
    onCategoriesOpen = () => {},
    onAddingFile,
    ...props
}: ItemDetailsFormProps & React.HTMLAttributes<HTMLDivElement>) => {
    const [isAddingFile, setIsAddingFile] = useState(false);

    const value = useMemo(
        () => ({
            isAddingFile,
            setIsAddingFile: (isAdding: boolean) => {
                setIsAddingFile(isAdding);
                onAddingFile?.(isAdding);
            },
            onCategoriesOpen,
        }),
        [isAddingFile, onAddingFile, onCategoriesOpen],
    );

    return (
        <ItemDetailsContext.Provider value={value}>
            <div {...props}>{children}</div>
        </ItemDetailsContext.Provider>
    );
};

interface ItemDetailsFormTitleProps extends ComponentPropsWithoutRef<'h2'> {}
const ItemDetailsFormTitle = (props: ItemDetailsFormTitleProps) => {
    const { className = '', ...restProps } = props;
    const { isEditing } = useItemForm();

    return (
        <h2 className={twMerge('text-2xl font-medium', className)} {...restProps}>
            {isEditing ? 'Edit' : 'New'} Item Details
        </h2>
    );
};

interface ItemDetailsFormNameProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormName = (props: ItemDetailsFormNameProps) => {
    const { className = '', ...restProps } = props;
    const { item, sponsoredItem } = useItemForm();
    const { control, path } = Form.useFormContext<ItemFormData>();

    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;
    const currentSponsorItem = item?.sponsor_item ?? sponsoredItem;

    return (
        <div className={twMerge('flex flex-col gap-y-2', className)} {...restProps}>
            <div className={'flex items-center gap-x-2'}>
                <Form.Label className={'text-base'} name={path('name')}>
                    Name
                </Form.Label>
                <AuctionTooltip.Root>
                    <AuctionTooltip.Trigger />
                    <AuctionTooltip.Content>
                        Use a name that clearly indicates to donors what auction item is being
                        listed.
                    </AuctionTooltip.Content>
                </AuctionTooltip.Root>
            </div>
            <Form.Container>
                <Form.TextInputRoot
                    control={control}
                    name={'name'}
                    rules={{
                        required: 'Must provide an item name.',
                    }}
                    disabled={isSponsored && !currentSponsorItem?.config_data?.can_change_name}
                >
                    <Form.TextInputForm />
                </Form.TextInputRoot>
                <Form.Error name={path('name')} />
            </Form.Container>
        </div>
    );
};

interface ItemDetailsFormSponsorDescriptionProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormSponsorDescription = (props: ItemDetailsFormSponsorDescriptionProps) => {
    const { className = '', ...restProps } = props;
    const { item, sponsoredItem } = useItemForm();
    const { control, watch, path } = Form.useFormContext<ItemFormData>();

    const [hideSponsorDescription] = watch(['hideSponsorDescription']);
    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;
    const currentSponsorItem = item?.sponsor_item ?? sponsoredItem;

    return (
        <div className={twMerge('flex flex-col gap-y-6', className)} {...restProps}>
            {(!isSponsored ||
                (isSponsored && !!currentSponsorItem?.config_data?.can_prepend_description)) && (
                <div className="flex flex-col gap-y-2">
                    <div className={'flex items-center gap-x-2'}>
                        <Form.UseFormLabel className={'text-base'} name={path('description')}>
                            Description
                        </Form.UseFormLabel>
                        <AuctionTooltip.Root>
                            <AuctionTooltip.Trigger />
                            <AuctionTooltip.Content>
                                Describe the auction item using clear and accurate details.
                            </AuctionTooltip.Content>
                        </AuctionTooltip.Root>
                    </div>
                    <Wysiwyg.Root>
                        <UseFormWysiwyg
                            name={'description'}
                            control={control}
                            inline={false}
                            init={WysiwygConfig}
                        />
                    </Wysiwyg.Root>
                </div>
            )}
            {isSponsored && !!currentSponsorItem?.config_data?.can_hide_description && (
                <div className={'col-span-full flex justify-between'}>
                    <div className={'flex flex-col gap-y-2'}>
                        <p className={'text-base font-medium'}>Hide Sponsor Description</p>
                    </div>
                    <Form.Switch name={'hideSponsorDescription'} control={control} />
                </div>
            )}
            {isSponsored && !!currentSponsorItem?.description && !hideSponsorDescription && (
                <div className="flex flex-col gap-y-2">
                    <Label className={'text-base'}>
                        Description provided by {currentSponsorItem?.sponsor?.name}
                    </Label>
                    <Box className={'p-4'}>
                        <givebutter-html-renderer
                            html={currentSponsorItem?.description}
                        ></givebutter-html-renderer>
                    </Box>
                </div>
            )}
        </div>
    );
};

interface ItemDetailsFormCategoriesProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormCategories = (props: ItemDetailsFormCategoriesProps) => {
    const { className = '', ...restProps } = props;
    const { onCategoriesOpen } = useItemDetailsForm();
    const { categories } = useItemForm();
    const { control } = Form.useFormContext<ItemFormData>();

    return (
        <div className={twMerge('sm:w-1/2', className)} {...restProps}>
            <Form.Container>
                <Form.Label name="category_id">Category (optional)</Form.Label>
                <Form.SingleSelect control={control} name="category_id">
                    <SingleSelect.Trigger aria-label="Category" />
                    <SingleSelect.Content>
                        {categories && categories.length > 0 && (
                            <>
                                <SingleSelect.Item value="NONE">
                                    <SingleSelect.ItemText>None</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                {categories.map(cat => {
                                    return (
                                        <SingleSelect.Item value={cat.id.toString()} key={cat.id}>
                                            <SingleSelect.ItemText>
                                                {!!cat.icon && `${cat.icon} `}
                                                {cat.name}
                                            </SingleSelect.ItemText>
                                            {!cat.is_published && (
                                                <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium">
                                                    Hidden
                                                </span>
                                            )}
                                        </SingleSelect.Item>
                                    );
                                })}
                            </>
                        )}

                        <Button
                            variant="none"
                            color="neutral"
                            size="sm"
                            className="w-full justify-start px-1 py-2 font-medium"
                            onClick={() => onCategoriesOpen(true)}
                        >
                            <FontAwesomeIcon icon={faPlus} /> New category
                        </Button>
                    </SingleSelect.Content>
                </Form.SingleSelect>
            </Form.Container>
        </div>
    );
};

interface ItemDetailsFormPhotosProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormPhotos = (props: ItemDetailsFormPhotosProps) => {
    const { className = '', ...restProps } = props;
    const { item, sponsoredItem } = useItemForm();
    const { control, path } = Form.useFormContext<ItemFormData>();
    const { isAddingFile, setIsAddingFile } = useItemDetailsForm();

    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;
    const currentSponsorItem = item?.sponsor_item ?? sponsoredItem;

    return (
        (!isSponsored || (isSponsored && !!currentSponsorItem?.config_data?.can_add_media)) && (
            <div className={twMerge('flex flex-col gap-y-2', className)} {...restProps}>
                <Form.UseFormLabel
                    className={'text-base'}
                    name={path('pictures')}
                    data-intercom-target="auction-items-photos"
                >
                    Photos
                </Form.UseFormLabel>
                <p className={'text-base text-gray-600'}>
                    Add detailed images of your auction item.
                </p>
                <UseFormUpload
                    acceptedFileTypes={['image/svg', 'image/jpg', 'image/jpeg', 'image/png']}
                    labelFileTypeNotAllowed={
                        'Please convert your image to a .png, .jpg, or .svg and re-upload.'
                    }
                    name={'pictures'}
                    control={control}
                    onaddfilestart={() => setIsAddingFile(true)}
                    onupdatefiles={() => setIsAddingFile(false)}
                    disabled={isAddingFile}
                />
                <Form.UseFormError name={path('pictures')} />
            </div>
        )
    );
};

interface ItemDetailsFormSponsorMediaProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormSponsorMedia = (props: ItemDetailsFormSponsorMediaProps) => {
    const { itemForm, item, sponsoredItem } = useItemForm();
    const { control, watch } = Form.useFormContext<ItemFormData>();

    const [hideSponsorMedia] = watch(['hideSponsorMedia']);

    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;
    const currentSponsorItem = item?.sponsor_item ?? sponsoredItem;

    return (
        <div {...props}>
            {isSponsored && !!currentSponsorItem?.config_data?.can_hide_media && (
                <div className={'col-span-full flex justify-between'}>
                    <div className={'flex flex-col gap-y-2'}>
                        <p className={'text-base font-medium'}>Hide Sponsor Media</p>
                    </div>
                    <Form.Switch name={'hideSponsorMedia'} control={control} />
                </div>
            )}
            {!!itemForm?.media && !hideSponsorMedia && (
                <div className={'flex flex-col'}>
                    <h3 className="text-sm font-medium">
                        Media provided by {currentSponsorItem?.sponsor?.name}
                    </h3>
                    <Slider
                        className="mainSlider"
                        arrows={true}
                        dots
                        appendDots={(dots: any[]) => (
                            <div
                                style={{
                                    // slick only respects inline styling
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(8, 1fr)',
                                    columnGap: '0.25rem',
                                    rowGap: '0.25rem',
                                    flexWrap: 'wrap',
                                    position: 'relative',
                                    bottom: '0rem',
                                }}
                            >
                                {dots?.map(item => ({
                                    ...item,
                                    type: 'div',
                                    props: {
                                        ...item.props,
                                        className: 'flex',
                                    },
                                }))}
                            </div>
                        )}
                        customPaging={i => (
                            <div
                                key={`thumb-${itemForm?.media?.[i]?.url}`}
                                className={`h-10 w-full cursor-pointer md:h-20`}
                            >
                                <img
                                    className={`h-full w-auto object-cover`}
                                    src={itemForm?.media?.[i]?.url}
                                    alt={itemForm?.media?.[i]?.alt}
                                />
                            </div>
                        )}
                    >
                        {itemForm?.media?.map(media => (
                            <div key={`main-${media?.url}`} className="h-full">
                                <img
                                    src={media?.url}
                                    alt={media?.alt}
                                    className={`h-auto w-full rounded object-cover`}
                                />
                            </div>
                        ))}
                    </Slider>
                </div>
            )}
        </div>
    );
};

interface ItemDetailsFormSellingFormatProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormSellingFormat = (props: ItemDetailsFormSellingFormatProps) => {
    const { className = '', ...restProps } = props;
    const { itemForm, item, sponsoredItem, isDuplicating } = useItemForm();
    const { control, path } = Form.useFormContext<ItemFormData>();

    const { allowsBuyNow, allowBids } = itemForm;
    const didStart = !isDuplicating && !!item && !item.can_delete;
    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;

    return (
        <div className={twMerge('flex flex-col gap-y-4', className)} {...restProps}>
            <div className={'flex items-center gap-x-2'}>
                <Form.Label className={'text-base'} name={path('allowBids')}>
                    Selling Format
                </Form.Label>
                <AuctionTooltip.Root>
                    <AuctionTooltip.Trigger />
                    <AuctionTooltip.Content>
                        Choose how you want to list this auction item for sale.
                    </AuctionTooltip.Content>
                </AuctionTooltip.Root>
            </div>
            {didStart && (
                <p className={'font-medium italic'}>
                    Selling format cannot be changed after item has received bids.
                </p>
            )}
            <Form.UseFormRadioGroup
                name={'allowBids'}
                control={control}
                orientation={'vertical'}
                variant={'card'}
            >
                <RadioGroup.Item
                    id={'allow-bids-auction'}
                    value={'auction'}
                    disabled={(isSponsored && !getAllowBids(allowBids)) || didStart}
                    className={'flex gap-x-3'}
                    size={'sm'}
                >
                    <RadioGroup.IndicatorContainer>
                        <RadioGroup.Indicator />
                    </RadioGroup.IndicatorContainer>
                    <div className={'flex flex-1 flex-col'}>
                        <Form.Label
                            className={'text-base'}
                            name={path('allowBids')}
                            htmlFor={'allow-bids-auction'}
                        >
                            Auction Style
                        </Form.Label>
                        <p className={'text-base text-gray-600'}>
                            Item is sold to the highest bidder or purchased at an optional
                            Buy-It-Now price.
                        </p>
                    </div>
                </RadioGroup.Item>
                <RadioGroup.Item
                    id={'allow-bids-fixed'}
                    disabled={didStart || (isSponsored && !allowsBuyNow)}
                    value={'fixed'}
                    className={'flex gap-x-3'}
                    size={'sm'}
                >
                    <RadioGroup.IndicatorContainer>
                        <RadioGroup.Indicator />
                    </RadioGroup.IndicatorContainer>
                    <div className={'flex flex-1 flex-col'}>
                        <Form.Label
                            className={'text-base'}
                            name={path('allowBids')}
                            htmlFor={'allow-bids-fixed'}
                        >
                            Fixed Price
                        </Form.Label>
                        <p className={'text-base text-gray-600'}>
                            Item can only be purchased at a fixed Buy-It-Now price.
                        </p>
                    </div>
                </RadioGroup.Item>
            </Form.UseFormRadioGroup>
        </div>
    );
};

interface ItemDetailsFormStartingPriceProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormStartingPrice = (props: ItemDetailsFormStartingPriceProps) => {
    const { className = '', ...restProps } = props;
    const { item, isDraft, isDuplicating } = useItemForm();
    const { control, watch, path } = Form.useFormContext<ItemFormData>();

    const [allowBidsRaw] = watch(['allowBids']);
    const isFixed = !getAllowBids(allowBidsRaw);
    const didStart = !isDuplicating && !!item && !item.can_delete;

    return (
        !isFixed && (
            <div
                className={twMerge('col-span-full flex flex-col gap-y-2 md:col-span-1', className)}
                {...restProps}
            >
                <div className={'flex items-center gap-x-2'}>
                    <Form.Label className={'text-base'} name={path('startBid')}>
                        Starting Price
                    </Form.Label>
                    <AuctionTooltip.Root>
                        <AuctionTooltip.Trigger />
                        <AuctionTooltip.Content>
                            Sets the lowest amount at which a bid can be placed.
                        </AuctionTooltip.Content>
                    </AuctionTooltip.Root>
                </div>
                <Form.TextInputRoot
                    disabled={didStart}
                    control={control}
                    name={'startBid'}
                    rules={{
                        required: !isDraft && !isFixed && 'Must set a starting bid amount.',
                    }}
                    inputRootProps={{
                        mask: 'decimal',
                    }}
                >
                    <InputCurrencyIcon />
                    <Form.TextInputForm placeholder="1" />
                </Form.TextInputRoot>
                <Form.Error name={path('startBid')} />
            </div>
        )
    );
};

interface ItemDetailsFormBuyNowProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormBuyNow = (props: ItemDetailsFormBuyNowProps) => {
    const { className = '', ...restProps } = props;
    const { item, isDraft, isDuplicating, itemForm, sponsoredItem } = useItemForm();
    const { control, watch, path } = Form.useFormContext<ItemFormData>();

    const [startPrice, allowBidsRaw] = watch(['startBid', 'allowBids']);

    const isFixed = !getAllowBids(allowBidsRaw);
    const allowBuyNow = !!itemForm.allowsBuyNow;
    const didStart = !isDuplicating && !!item && !item.can_delete;
    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;

    return (
        allowBuyNow && (
            <div
                className={twMerge(`col-span-full flex flex-col gap-y-2 md:col-span-1`, className)}
                {...restProps}
            >
                <div className={'flex items-center gap-x-2'}>
                    <Form.UseFormLabel className={'text-base'} name={path('fixedPrice')}>
                        Buy Now Price
                    </Form.UseFormLabel>

                    <AuctionTooltip.Root>
                        <AuctionTooltip.Trigger />
                        <AuctionTooltip.Content>
                            Sets a fixed-price that bidders can purchase the item outright.
                        </AuctionTooltip.Content>
                    </AuctionTooltip.Root>
                    {!isFixed && <p className={'text-gray-500'}>(optional)</p>}
                </div>
                <Form.TextInputRoot
                    disabled={didStart}
                    name={'fixedPrice'}
                    control={control}
                    rules={{
                        validate: (value: string) => {
                            if (isDraft) {
                                return true;
                            }

                            if (isFixed) {
                                if (value.length === 0) {
                                    return 'Buy now price is required.';
                                }
                            }

                            if (
                                !isFixed &&
                                value.length > 0 &&
                                value !== '0.00' &&
                                startPrice?.length > 0 &&
                                unmaskNumber(startPrice) > unmaskNumber(value)
                            ) {
                                return 'Must be higher than starting price.';
                            }

                            if (
                                isSponsored &&
                                value !== '0.00' && // allow 0 to disable buy-now
                                unmaskNumber(value) < unmaskNumber(itemForm.reservePriceMinimum)
                            ) {
                                return `Must be equal or higher than sponsored minimum price of $${maskDecimal(
                                    itemForm.reservePriceMinimum,
                                )}.`;
                            }

                            return true;
                        },
                    }}
                    inputRootProps={{
                        mask: 'decimal',
                    }}
                >
                    <InputCurrencyIcon />
                    <Form.TextInputForm placeholder="5" />
                </Form.TextInputRoot>
                <Form.UseFormError name={path('fixedPrice')} />
            </div>
        )
    );
};

interface ItemDetailsFormReservePriceProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormReservePrice = (props: ItemDetailsFormReservePriceProps) => {
    const { className = '', ...restProps } = props;
    const { itemForm, item, isDraft, sponsoredItem } = useItemForm();
    const { control, watch, path } = Form.useFormContext<ItemFormData>();
    const [allowBidsRaw, reservePrice] = watch(['allowBids', 'reservePrice']);

    const isFixed = !getAllowBids(allowBidsRaw);
    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;

    return (
        !isFixed && (
            <div
                className={twMerge('col-span-full flex flex-col gap-y-2 md:col-span-1', className)}
                {...restProps}
            >
                <div className={'flex items-center gap-x-2'}>
                    <Form.Label className={'text-base'} name={path('reservePrice')}>
                        Reserve Price
                    </Form.Label>
                    {isSponsored && <SponsorReservePriceInfo />}
                    {!isSponsored && (
                        <AuctionTooltip.Root>
                            <AuctionTooltip.Trigger />
                            <AuctionTooltip.Content>
                                Sets the lowest acceptable winning price for this item. Items that
                                do not reach their set reserve price have no winning bidder.
                            </AuctionTooltip.Content>
                        </AuctionTooltip.Root>
                    )}
                    {!isSponsored && !reservePrice && <p className={'text-gray-500'}>(optional)</p>}
                </div>
                <Form.TextInputRoot
                    control={control}
                    name={'reservePrice'}
                    rules={{
                        validate: (value: string) => {
                            if (isDraft) {
                                return true;
                            }

                            if (
                                isSponsored &&
                                ((!!itemForm.reservePriceMinimum && !value.length) ||
                                    unmaskNumber(value) <
                                        unmaskNumber(itemForm.reservePriceMinimum))
                            ) {
                                return `Must be equal or higher than sponsored item reserve price of $${maskDecimal(
                                    itemForm.reservePriceMinimum,
                                )}.`;
                            }

                            return true;
                        },
                    }}
                    inputRootProps={{
                        mask: 'decimal',
                    }}
                >
                    <InputCurrencyIcon />
                    <Form.TextInputForm placeholder="5" />
                </Form.TextInputRoot>
                <Form.UseFormError name={path('reservePrice')} />
            </div>
        )
    );
};

interface ItemDetailsFormItemValueProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormItemValue = (props: ItemDetailsFormItemValueProps) => {
    const { className = '', ...restProps } = props;
    const { itemForm, item, isDraft, sponsoredItem, isDuplicating } = useItemForm();
    const { control, watch, path } = Form.useFormContext<ItemFormData>();
    const retailPrice = watch('retailPrice');
    const { taxDeductibilitySettings = false } = useFlags();

    const currentSponsorItem = item?.sponsor_item ?? sponsoredItem;
    const didStart = !isDuplicating && !!item && !item.can_delete;
    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;

    return (
        <div
            className={twMerge('col-span-full flex flex-col gap-y-2 md:col-span-1', className)}
            {...restProps}
        >
            <div className={'flex items-center gap-x-2'}>
                <Form.Label className={'text-base'} name={path('retailPrice')}>
                    {taxDeductibilitySettings ? 'Fair market value' : 'Item Value'}
                </Form.Label>
                <AuctionTooltip.Root>
                    <AuctionTooltip.Trigger />
                    <AuctionTooltip.Content>
                        {taxDeductibilitySettings ? (
                            <>
                                Auction items will only show a tax-deductible amount on receipts and
                                end-of-year statements if a Fair Market Value is provided. The
                                tax-deductible amount will be the Winning Bid Amount minus Fair
                                Market Value, per IRS guidelines.
                            </>
                        ) : (
                            <>Indicates the retail price of the item being listed.</>
                        )}
                    </AuctionTooltip.Content>
                </AuctionTooltip.Root>
                {!isSponsored && !retailPrice && <p className={'text-gray-500'}>(optional)</p>}
            </div>
            <Form.TextInputRoot
                disabled={
                    didStart ||
                    (isSponsored && !currentSponsorItem?.config_data?.can_change_retail_price)
                }
                control={control}
                name={'retailPrice'}
                rules={{
                    validate: value => {
                        if (isDraft) {
                            return true;
                        }

                        if (
                            (isSponsored && !!itemForm.retailPriceMinimum && !value?.length) ||
                            (isSponsored &&
                                unmaskNumber(value) <
                                    unmaskNumber(itemForm.retailPriceMinimum ?? 0))
                        ) {
                            return `Must be equal or higher than sponsored item retail price of $${maskDecimal(
                                itemForm.retailPriceMinimum ?? '',
                            )}.`;
                        }

                        return true;
                    },
                }}
                inputRootProps={{
                    mask: 'decimal',
                }}
            >
                <InputCurrencyIcon />
                <Form.TextInputForm placeholder="50" />
            </Form.TextInputRoot>
            <Form.UseFormError name={path('retailPrice')} />
        </div>
    );
};

interface ItemDetailsFormBidIncrementProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormBidIncrement = (props: ItemDetailsFormBidIncrementProps) => {
    const { className = '', ...restProps } = props;
    const { item, isDraft, isDuplicating } = useItemForm();
    const { control, watch, path } = Form.useFormContext<ItemFormData>();
    const [allowBidsRaw] = watch(['allowBids']);

    const isFixed = !getAllowBids(allowBidsRaw);
    const didStart = !isDuplicating && !!item && !item.can_delete;

    return (
        !isFixed && (
            <div
                className={twMerge('col-span-full flex flex-col gap-y-2 md:col-span-1', className)}
                {...restProps}
            >
                <div className={'flex items-center gap-x-2'}>
                    <Form.Label className={'text-base'} name={path('bidIncrement')}>
                        Bid Increment
                    </Form.Label>
                    <AuctionTooltip.Root>
                        <AuctionTooltip.Trigger />
                        <AuctionTooltip.Content>
                            Sets the minimum amount an auction price must be raised during bidding.
                        </AuctionTooltip.Content>
                    </AuctionTooltip.Root>
                </div>
                <Form.TextInputRoot
                    disabled={didStart}
                    name={'bidIncrement'}
                    control={control}
                    rules={{
                        validate: (value: string) => {
                            const numValue = unmaskNumber(value);

                            if (isDraft && numValue !== 0) {
                                return true;
                            }

                            if (!isFixed && !isNaN(numValue) && numValue < 1) {
                                return 'Bid increment must be $1 or greater';
                            }

                            return true;
                        },
                    }}
                    inputRootProps={{
                        mask: 'decimal',
                    }}
                >
                    <Form.TextInputForm placeholder="1" />
                </Form.TextInputRoot>
                <Form.Error name={path('bidIncrement')} />
            </div>
        )
    );
};

interface ItemDetailsFormTotalQuantityProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormTotalQuantity = (props: ItemDetailsFormTotalQuantityProps) => {
    const { className = '', ...restProps } = props;
    const { control, watch, path } = Form.useFormContext<ItemFormData>();
    const [allowBidsRaw] = watch(['allowBids']);

    const isFixed = !getAllowBids(allowBidsRaw);

    return (
        isFixed && (
            <div
                className={twMerge('col-span-full flex flex-col gap-y-2 md:col-span-1', className)}
                {...restProps}
            >
                <div className={'flex items-center gap-x-2'}>
                    <Form.Label className={'text-base'} name={path('quantity')}>
                        Total Quantity
                    </Form.Label>
                    <AuctionTooltip.Root>
                        <AuctionTooltip.Trigger />
                        <AuctionTooltip.Content>
                            Sets the total quantity when selling multiple identical items.
                        </AuctionTooltip.Content>
                    </AuctionTooltip.Root>
                </div>
                <Form.TextInputRoot
                    control={control}
                    name={'quantity'}
                    inputRootProps={{
                        mask: 'whole-number',
                    }}
                >
                    <Form.TextInputForm placeholder="1" />
                </Form.TextInputRoot>
                <Form.Error name={path('quantity')} />
            </div>
        )
    );
};

interface ItemDetailsFormShippingProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormShipping = (props: ItemDetailsFormShippingProps) => {
    const { item, isDuplicating, itemForm, sponsoredItem } = useItemForm();
    const { control, watch, path } = Form.useFormContext<ItemFormData>();

    const [isShippingRequired] = watch(['isShippingRequired']);

    const didStart = !isDuplicating && !!item && !item.can_delete;
    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;

    return (
        <div {...props}>
            <div className={'col-span-full flex justify-between'}>
                <div className={'flex flex-col gap-y-2'}>
                    <p className={'text-base font-medium'}>Requires Shipping</p>
                    <p className={'text-base text-gray-600'}>
                        Indicates the item needs to be fulfilled by mail.
                    </p>
                </div>
                <Form.Switch
                    name={'isShippingRequired'}
                    control={control}
                    disabled={didStart || (isSponsored && !!itemForm.isShippingRequired)}
                />
            </div>
            <div
                className={`col-span-full flex flex-col gap-y-2 md:col-span-1 ${
                    !isShippingRequired ? 'opacity-60' : ''
                }`}
            >
                <div className={'flex items-center gap-x-2'}>
                    <Form.Label className={'text-base'} name={path('shippingPrice')}>
                        Shipping Costs
                    </Form.Label>
                    <AuctionTooltip.Root>
                        <AuctionTooltip.Trigger />
                        <AuctionTooltip.Content>
                            Adds shipping and handling costs to the final price of an auction item
                            when the item is being fulfilled by mail.
                        </AuctionTooltip.Content>
                    </AuctionTooltip.Root>
                    <p className={'text-gray-500'}>(optional)</p>
                </div>
                <Form.TextInputRoot
                    disabled={didStart || !isShippingRequired}
                    control={control}
                    name={'shippingPrice'}
                    inputRootProps={{
                        mask: 'decimal',
                    }}
                >
                    <InputCurrencyIcon />
                    <Form.TextInputForm placeholder="5.00" />
                </Form.TextInputRoot>
            </div>
        </div>
    );
};

interface ItemDetailsFormInPersonProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormInPerson = (props: ItemDetailsFormInPersonProps) => {
    const { className = '', ...restProps } = props;
    const { inPersonAuctionItems = false } = useFlags();
    const { control, path } = Form.useFormContext<ItemFormData>();

    return (
        inPersonAuctionItems && (
            <div className={twMerge('flex flex-col gap-y-4', className)} {...restProps}>
                <div className={'flex justify-between'}>
                    <div className={'flex flex-col gap-y-2'}>
                        <Form.Label className={'text-base'} name={path('inPerson')}>
                            Mark as in-person item
                        </Form.Label>
                        <p className={'text-base text-gray-600'}>
                            Indicates that bidding and collection of payment may happen outside our
                            platform.
                        </p>
                    </div>
                    <Form.Switch name={'inPerson'} control={control} />
                </div>
            </div>
        )
    );
};

interface ItemDetailsFormVisibilityProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormVisibility = (props: ItemDetailsFormVisibilityProps) => {
    const { className = '', ...restProps } = props;
    const { control, path } = Form.useFormContext<ItemFormData>();

    return (
        <div className={twMerge('flex flex-col gap-y-3 p-8', className)} {...restProps}>
            <Form.Label className={'text-base'} name={path('visibility')}>
                Visibility
            </Form.Label>
            <Form.UseFormRadioGroup
                orientation={'horizontal'}
                name={'visibility'}
                control={control}
                className={'flex-col sm:flex-col'}
                variant={'card'}
            >
                <RadioGroup.Item
                    id={'visibility-public'}
                    value={'public'}
                    className={'flex gap-x-3'}
                    size={'sm'}
                >
                    <RadioGroup.IndicatorContainer>
                        <RadioGroup.Indicator />
                    </RadioGroup.IndicatorContainer>
                    <div className={'flex flex-1 flex-col'}>
                        <Form.Label
                            className={'text-base'}
                            name={path('visibility')}
                            htmlFor={'visibility-public'}
                        >
                            Public
                        </Form.Label>
                        <p className={'text-base text-gray-600'}>
                            This item will be viewable by everyone.
                        </p>
                    </div>
                </RadioGroup.Item>
                <RadioGroup.Item
                    id={'visibility-private'}
                    value={'private'}
                    className={'flex gap-x-3'}
                    size={'sm'}
                >
                    <RadioGroup.IndicatorContainer>
                        <RadioGroup.Indicator />
                    </RadioGroup.IndicatorContainer>
                    <div className={'flex flex-1 flex-col'}>
                        <Form.Label
                            className={'text-base'}
                            name={path('visibility')}
                            htmlFor={'visibility-private'}
                        >
                            Private
                        </Form.Label>
                        <p className={'text-base text-gray-600'}>
                            This item is only accessible via shareable link.
                        </p>
                    </div>
                </RadioGroup.Item>
            </Form.UseFormRadioGroup>
        </div>
    );
};

interface ItemDetailsFormFulfillmentDetailsProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormFulfillmentDetails = (props: ItemDetailsFormFulfillmentDetailsProps) => {
    const { className = '', ...restProps } = props;
    const { control, path } = Form.useFormContext<ItemFormData>();

    return (
        <div className={twMerge('flex flex-col gap-y-2', className)} {...restProps}>
            <div className={'flex items-center gap-x-2'}>
                <Form.Label className={'text-base'} name={path('purchaseMessage')}>
                    Fulfillment Details
                </Form.Label>
                <AuctionTooltip.Root>
                    <AuctionTooltip.Trigger />
                    <AuctionTooltip.Content>
                        Fulfillment details are sent alongside item receipt once payment has been
                        received.
                    </AuctionTooltip.Content>
                </AuctionTooltip.Root>
                <p className={'text-gray-500'}>(optional)</p>
            </div>
            <p className={'text-base text-gray-600'}>
                Add any information necessary for the winning bidder to collect their item.
            </p>
            <Wysiwyg.Root>
                <UseFormWysiwyg
                    name={'purchaseMessage'}
                    control={control}
                    inline={false}
                    init={WysiwygConfig}
                />
            </Wysiwyg.Root>
        </div>
    );
};

interface ItemDetailsFormSponsorPurchaseMessageProps extends ComponentPropsWithoutRef<'div'> {}
const ItemDetailsFormSponsorPurchaseMessage = (
    props: ItemDetailsFormSponsorPurchaseMessageProps,
) => {
    const { item, sponsoredItem } = useItemForm();
    const { control, watch } = Form.useFormContext<ItemFormData>();
    const [hideSponsorPurchaseMessage] = watch(['hideSponsorPurchaseMessage']);

    const isSponsored = !!item?.sponsor_item?.sponsor?.id || !!sponsoredItem?.id;
    const currentSponsorItem = item?.sponsor_item ?? sponsoredItem;

    return (
        <div {...props}>
            {isSponsored && !!currentSponsorItem?.config_data?.can_hide_purchase_message && (
                <div className={'col-span-full flex justify-between'}>
                    <div className={'flex flex-col gap-y-2'}>
                        <p className={'text-base font-medium'}>Hide Sponsor Purchase Message</p>
                    </div>
                    <Form.Switch name={'hideSponsorPurchaseMessage'} control={control} />
                </div>
            )}
            {isSponsored &&
                !hideSponsorPurchaseMessage &&
                !!currentSponsorItem?.purchase_message && (
                    <div className="flex flex-col gap-y-2">
                        <Label className={'text-base'}>
                            Purchase Message provided by {currentSponsorItem?.sponsor?.name}
                        </Label>
                        <Box className={'p-4'}>
                            <givebutter-html-renderer
                                html={currentSponsorItem?.purchase_message}
                            ></givebutter-html-renderer>
                        </Box>
                    </div>
                )}
        </div>
    );
};

const Root = ItemDetailsForm;
const Title = ItemDetailsFormTitle;
const Name = ItemDetailsFormName;
const SponsorDescription = ItemDetailsFormSponsorDescription;
const Categories = ItemDetailsFormCategories;
const Photos = ItemDetailsFormPhotos;
const SponsorMedia = ItemDetailsFormSponsorMedia;

const SellingFormat = ItemDetailsFormSellingFormat;
const StartingPrice = ItemDetailsFormStartingPrice;
const BuyNow = ItemDetailsFormBuyNow;
const ReservePrice = ItemDetailsFormReservePrice;
const ItemValue = ItemDetailsFormItemValue;

const BidIncrement = ItemDetailsFormBidIncrement;
const TotalQuantity = ItemDetailsFormTotalQuantity;
const Shipping = ItemDetailsFormShipping;
const InPerson = ItemDetailsFormInPerson;
const Visibility = ItemDetailsFormVisibility;
const FulfillmentDetails = ItemDetailsFormFulfillmentDetails;
const SponsorPurchaseMessage = ItemDetailsFormSponsorPurchaseMessage;

export {
    useItemDetailsForm,
    //
    Root,
    Title,
    Name,
    SponsorDescription,
    Categories,
    Photos,
    SponsorMedia,
    SellingFormat,
    StartingPrice,
    BuyNow,
    ReservePrice,
    ItemValue,
    BidIncrement,
    TotalQuantity,
    Shipping,
    InPerson,
    Visibility,
    FulfillmentDetails,
    SponsorPurchaseMessage,
    //
    ItemDetailsForm,
    ItemDetailsFormTitle,
    ItemDetailsFormName,
    ItemDetailsFormSponsorDescription,
    ItemDetailsFormCategories,
    ItemDetailsFormPhotos,
    ItemDetailsFormSponsorMedia,
    ItemDetailsFormSellingFormat,
    ItemDetailsFormStartingPrice,
    ItemDetailsFormBuyNow,
    ItemDetailsFormReservePrice,
    ItemDetailsFormItemValue,
    ItemDetailsFormBidIncrement,
    ItemDetailsFormTotalQuantity,
    ItemDetailsFormShipping,
    ItemDetailsFormInPerson,
    ItemDetailsFormVisibility,
    ItemDetailsFormFulfillmentDetails,
    ItemDetailsFormSponsorPurchaseMessage,
};
